import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/shared/providers/AuthProvider";
import { useTenantContext } from "@/shared/providers/TenantProvider";
// TODO: Re-enable when Gate 2 (analytics.enabled feature flag) is re-activated for PROD.
// import { useRuntimeConfigContext } from "@/shared/providers/RuntimeConfigProvider";
import { pendoInitialize, pendoIdentify, pendoPageLoad, isPendoReady } from "@/shared/analytics/pendo";
// TODO: Re-import env when uncommenting the production gate (Gate 1).
// import { env } from "@/config/env";

/**
 * PendoTracker — invisible side-effect component.
 *
 * Responsibilities:
 *  1. Initialises Pendo once the user is authenticated (AC2, AC3).
 *  2. Re-identifies Pendo whenever the user or tenant changes (AC3).
 *  3. Fires a synthetic `pendo.pageLoad()` on every SPA route change (AC4).
 *
 * Placement requirements:
 *  - Must be a descendant of `AuthProvider` and `TenantProvider` (to read user + tenantId).
 *  - Must be a descendant of React Router's `RouterProvider` (to call `useLocation`).
 *  - The Pendo async loader snippet in index.html loads the agent independently;
 *    this component only calls the agent API — it never injects a <script> tag.
 *
 * This component renders nothing — it exists purely for its side-effects.
 */
const PendoTracker: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { tenantId } = useTenantContext();
  // TODO: Re-enable when Gate 2 is re-activated for PROD.
  // const { isFeatureEnabled, loading: runtimeConfigLoading } = useRuntimeConfigContext();
  const location = useLocation();

  // Track whether Pendo has been initialised in this component instance.
  //
  // The ref is reset to false when the user logs out (isAuthenticated === false)
  // so that any subsequent re-authentication within the same component lifecycle
  // (e.g. session re-auth, impersonation switch) correctly calls pendoInitialize
  // again instead of pendoIdentify.  On a normal logout, AuthWrapper redirects to
  // /login and PendoTracker unmounts entirely — the reset is a belt-and-suspenders
  // guard for the in-place re-auth case (ECOPD-14897 relogin flow).
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      initializedRef.current = false;
    }
  }, [isAuthenticated]);

  // ------------------------------------------------------------------
  // 1. Initialise (or re-identify) Pendo whenever the authenticated user
  //    or tenantId changes.
  //
  //    THREE gates must all pass before Pendo is initialised — matching
  //    the legacy Rails app (analytics_support.rb / analytics_helper.rb):
  //
  //    Gate 1 — Production-only (legacy: Eco::PROD_ENV).
  //      Pendo never fires in dev/QA Vite builds.
  //
  //    Gate 2 — Runtime feature flag (legacy: eco_component?(:comp_analytics)).
  //      In the legacy Rails app, comp_analytics maps to the runtime-config key
  //      'analytics.enabled' (eco_component.rb:50).  Tenant admins can disable
  //      Pendo by setting this key to false without a code deploy.
  //      Default: false (opt-in).
  //      We wait for the runtime config to finish loading before evaluating.
  //
  //    Gate 3 — Authenticated user with valid ids.
  //      Never initialise with a placeholder visitor or account id.
  // ------------------------------------------------------------------
  useEffect(() => {
    // TODO: Re-enable Gate 1 and Gate 2 before the first production deployment.
    // Both gates are temporarily disabled while the application is in pre-production
    // so that Pendo can be verified end-to-end on dev/QA environments.
    //
    // Gate 1: production builds only (legacy parity: Eco::PROD_ENV).
    // Uncomment when promoting to PROD.
    // if (!env.IS_PRODUCTION) return;
    //
    // Gate 2: runtime feature flag (legacy: eco_component?(:comp_analytics) → 'analytics.enabled').
    // In the legacy Rails app this check was commented out in eco_pendo_scripts() so Pendo was
    // unconditionally enabled. Re-enable here for per-tenant opt-out control in React.
    // Requires runtimeConfigLoading === false before evaluating.
    // if (runtimeConfigLoading) return;
    // if (!isFeatureEnabled('analytics.enabled')) return;

    // Gate 3: user must be authenticated with valid ids (always enforced)
    if (!isAuthenticated || !user?.id || !tenantId) return;

    // Visitor ID format matches the legacy Rails app (analytics_support.rb):
    //   "#{system_id}_#{user_type}_#{user_id}"
    // where system_id = tenantId (instance_name), user_type = subcontractor type label.
    const visitorId = `${tenantId}_${user.userType || "unknown"}_${user.id}`;
    const accountId = tenantId;
    // Build visitorMeta without undefined values (exactOptionalPropertyTypes: true).
    const visitorMeta: { email?: string; full_name?: string } = {};
    if (user.email) visitorMeta.email = user.email;
    if (user.name) visitorMeta.full_name = user.name;

    if (isPendoReady()) {
      if (!initializedRef.current) {
        pendoInitialize(visitorId, accountId, visitorMeta);
        initializedRef.current = true;
      } else {
        pendoIdentify(visitorId, accountId, visitorMeta);
      }
      return;
    }

    // Pendo CDN script is still loading — poll every 200 ms (max 10 s) and
    // initialize as soon as the agent becomes available.
    let attempts = 0;
    const MAX_ATTEMPTS = 50; // 50 × 200 ms = 10 s
    const timer = setInterval(() => {
      attempts += 1;
      if (isPendoReady()) {
        clearInterval(timer);
        if (!initializedRef.current) {
          pendoInitialize(visitorId, accountId, visitorMeta);
          initializedRef.current = true;
        } else {
          pendoIdentify(visitorId, accountId, visitorMeta);
        }
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(timer);
      }
    }, 200);

    return () => clearInterval(timer);
  }, [isAuthenticated, user?.id, user?.email, user?.name, tenantId]);

  // ------------------------------------------------------------------
  // 2. Fire a Pendo synthetic page-load event on every SPA navigation.
  //
  //    Pendo's auto-detect only fires once for the initial page load when
  //    the script is injected. For a React SPA we must call pageLoad()
  //    manually on each route change so Pendo records each virtual page.
  //
  //    We only call it after Pendo has been initialized (initializedRef)
  //    to avoid recording anonymous/unauthenticated page views.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!initializedRef.current) return;
    pendoPageLoad();
  }, [location.pathname]);

  return null;
};

export default PendoTracker;